import { useEffect } from "react";
import { useQuery } from "@apollo/client";
import { CURRENT_USER } from "@/api/queries/currentUser";
import { useOrg } from "./useOrg";
import { useOrgPermissions } from "./useOrgPermissions";
import { PartnerFeatureEnum, UserRoleEnum } from "@/__generated__/types";

declare global {
  interface Window {
    hj?: (
      command: string,
      userId: string,
      attributes: Record<string, unknown>,
    ) => void;
  }
}

/**
 * Hook that identifies the current user with Hotjar
 * Sends user attributes including name, email, org, role, and RISE access
 */
export function useHotjarIdentify() {
  const { data: userData, loading: userLoading } = useQuery(CURRENT_USER);
  const { org, loading: orgLoading } = useOrg();
  const { hasOrgFeature, loading: permissionsLoading } = useOrgPermissions();

  useEffect(() => {
    if (userLoading || orgLoading || permissionsLoading) {
      return;
    }

    if (!userData?.me || !org) {
      return;
    }

    if (typeof window === "undefined" || !window.hj) {
      console.warn("Hotjar is not loaded");
      return;
    }

    const user = userData.me;
    const hasRise = hasOrgFeature(PartnerFeatureEnum.Rise);

    const attributes = {
      name: user.name || undefined,
      email: user.email || undefined,
      org: org.name || undefined,
      role: "role" in user ? (user.role as UserRoleEnum) : undefined,
      has_rise: hasRise,
    };

    try {
      window.hj("identify", user.uuid, attributes);
    } catch (error) {
      console.error("Failed to identify user with Hotjar:", error);
    }
  }, [
    userData,
    org,
    hasOrgFeature,
    userLoading,
    orgLoading,
    permissionsLoading,
  ]);
}
